// Chromium gates requests into a more private address space — a public page
// reaching 127.0.0.1, which is exactly how the Aspera SDK talks to Connect —
// behind the Local Network Access permission.
//
// The state is readable without making any such request, so we can tell
// whether asking would raise a prompt before we go anywhere near loopback.
// Browsers that do not gate loopback (Safari, Firefox, and a page served from
// loopback itself) report 'unsupported': there is nothing to ask for.
// Loopback is gated separately from the wider local network, and Aspera only
// ever talks to 127.0.0.1 — so ask about loopback where the browser knows that
// descriptor. The broader one can answer for a LAN permission we never wanted:
// granted there while loopback is still unanswered would have us start a
// session and raise the bare prompt this whole flow exists to avoid, and denied
// there would force HTTPS on someone loopback would have allowed.
const PERMISSION_NAMES = ['loopback-network', 'local-network-access'];

export const ACCESS = {
	GRANTED: 'granted',
	DENIED: 'denied',
	PROMPT: 'prompt',
	UNSUPPORTED: 'unsupported'
};

// How long to leave the browser's own dialog standing before deciding the user
// is not going to answer it. Dismissing that dialog leaves the state on
// 'prompt' and fires no event, so this cap is the only way out of that case —
// which is why it is not simply generous. Nobody is trapped by it either way:
// the onboarding modal keeps its Continue button live throughout.
export const ACCESS_DECISION_TIMEOUT = 30000;

const queryAccess = async() => {
	if( typeof navigator.permissions?.query !== 'function' ) {
		return null;
	}

	// Ordered most specific first; an unknown descriptor throws, which is how a
	// browser tells us to try the next one.
	const attempts = PERMISSION_NAMES.map((name) => () => navigator.permissions.query({ name }));

	for( const attempt of attempts ) {
		try {
			return await attempt();
		}
		catch{
			// this browser does not know that descriptor
		}
	}

	return null;
};

export const readAccess = async() => {
	const status = await queryAccess();

	// Nothing to ask for on a browser that gates neither.
	return status ? status.state : ACCESS.UNSUPPORTED;
};

/**
 * Calls back whenever the permission changes, including from the browser's own
 * site settings. Returns an unsubscribe function.
 */
export const watchAccess = (onChange) => {
	let status;
	let isCancelled = false;

	const handleChange = () => onChange(status.state);

	const watch = async() => {
		// The same lookup as readAccess, so a watcher can never end up following a
		// different permission than the one being read.
		const result = await queryAccess();

		if( isCancelled || !result ) {
			return;
		}

		status = result;
		status.addEventListener('change', handleChange);
	};
	watch();

	return () => {
		isCancelled = true;
		status?.removeEventListener('change', handleChange);
	};
};

/**
 * Resolves once the permission stops being unanswered — the user has clicked
 * Allow or Block — or with the state as it stands when the wait runs out.
 *
 * Reading it once is not enough while the browser's dialog is open. The SDK
 * gives up on loopback after about five seconds, long before most people finish
 * reading, and every decision downstream would then be taken against an answer
 * the user is still in the middle of giving.
 */
export const waitForAccess = (timeout = ACCESS_DECISION_TIMEOUT) => new Promise((resolve) => {
	let isSettled = false;
	let unwatch;

	const finish = (state) => {
		if( isSettled ) {
			return;
		}

		isSettled = true;
		clearTimeout(timer);
		unwatch?.();
		resolve(state);
	};

	// Nothing answered in time: report whatever it is now, which is very likely
	// the unanswered state a dismissed dialog leaves behind.
	const timer = setTimeout(() => readAccess().then(finish), timeout);

	const settleIfAnswered = (state) => {
		if( state !== ACCESS.PROMPT ) {
			finish(state);
		}
	};

	unwatch = watchAccess(settleIfAnswered);

	// Already answered before we got here — including the browsers that never
	// gate loopback, where there is nothing to wait for at all.
	readAccess().then(settleIfAnswered);
});
